import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BackButton from '../components/BackButton';
import { useFeedback } from '../components/FeedbackProvider';
import { prizeService } from '../services';
import './AdminPrizes.css';
import './AdminPrizeOrders.css';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

function getImageUrl(url) {
  if (!url) return 'https://via.placeholder.com/120x120/9b59b6/ffffff?text=Prize';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url}`;
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString() : '-';
}

function statusLabel(status) {
  return {
    pending: '待处理',
    completed: '已完成',
    cancelled: '已取消',
    mixed: '部分处理',
    empty: '空订单'
  }[status] || status || '-';
}

function deliveryLabel(order) {
  return Number(order?.has_physical || 0) ? '含实体商品' : '仅虚拟商品';
}

function formatCurrencyTotal(points = 0, movieTickets = 0) {
  const parts = [];
  if (Number(points) > 0) parts.push(`${points} 积分`);
  if (Number(movieTickets) > 0) parts.push(`${movieTickets} 电影票`);
  return parts.length ? parts.join(' / ') : '0';
}

function formatLineCost(item) {
  const cost = item.currency_cost ?? item.points_cost ?? 0;
  return item.currency_type === 'movie_ticket' ? `${cost} 电影票` : `${cost} 积分`;
}

function buildAddressText(order) {
  return [
    order?.recipient_name_snapshot,
    order?.phone_snapshot,
    `${order?.region_snapshot || ''}${order?.detail_address_snapshot || ''}`
  ].filter(Boolean).join(' ');
}

function AdminPrizeOrders() {
  const { confirm, prompt, success: showSuccess, error: showError } = useFeedback();
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({ status: 'pending', delivery_type: 'all', search: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const loadOrders = useCallback(async () => {
    const response = await prizeService.getAdminOrders(filters);
    setOrders(response.data || []);
  }, [filters]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        await loadOrders();
      } catch (err) {
        setError(err.response?.data?.message || '加载兑换订单失败');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [loadOrders]);

  const metrics = useMemo(() => {
    return orders.reduce((acc, order) => {
      acc.orders += 1;
      acc.pending += order.status === 'pending' ? 1 : 0;
      acc.mixed += order.status === 'mixed' ? 1 : 0;
      acc.physical += Number(order.has_physical || 0) ? 1 : 0;
      return acc;
    }, { orders: 0, pending: 0, mixed: 0, physical: 0 });
  }, [orders]);

  const openOrder = async (orderId) => {
    try {
      setDetailLoading(true);
      setError('');
      const response = await prizeService.getAdminOrder(orderId);
      setSelectedOrder(response.data);
    } catch (err) {
      setError(err.response?.data?.message || '加载订单详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshSelectedOrder = async (orderId) => {
    const response = await prizeService.getAdminOrder(orderId);
    setSelectedOrder(response.data);
  };

  const updateOrderStatus = async (order, status) => {
    let reason = '';
    if (status === 'cancelled') {
      const input = await prompt({
        title: '取消整单并退款',
        message: `确定要取消订单 #${order.id} 的所有待处理项目，并原路退回货币和库存吗？`,
        label: '取消原因（可选）',
        placeholder: '例如：用户申请取消、库存异常等',
        confirmText: '确认取消并退款',
        multiline: true
      });
      if (input === null) return;
      reason = input;
    } else {
      const ok = await confirm({
        title: '完成整单',
        message: `确定要把订单 #${order.id} 的所有待处理项目标记为完成吗？`,
        confirmText: '标记完成'
      });
      if (!ok) return;
    }

    try {
      setUpdating(true);
      setMessage('');
      setError('');
      await prizeService.updateOrderStatus(order.id, status, reason);
      setMessage(status === 'completed' ? '订单已完成' : '订单已取消并退款');
      await loadOrders();
      await refreshSelectedOrder(order.id);
    } catch (err) {
      setError(err.response?.data?.message || '更新订单状态失败');
    } finally {
      setUpdating(false);
    }
  };

  const updateLineStatus = async (item, status) => {
    let reason = '';
    if (status === 'cancelled') {
      const input = await prompt({
        title: '取消单项并退款',
        message: `确定要取消「${item.prize_name}」并原路退款吗？`,
        label: '取消原因（可选）',
        confirmText: '确认取消并退款',
        multiline: true
      });
      if (input === null) return;
      reason = input;
    } else {
      const ok = await confirm({
        title: '完成单项',
        message: `确定要把「${item.prize_name}」标记为完成吗？`,
        confirmText: '标记完成'
      });
      if (!ok) return;
    }

    try {
      setUpdating(true);
      setMessage('');
      setError('');
      await prizeService.updateRedemptionStatus(item.id, status, reason);
      setMessage(status === 'completed' ? '兑换项目已完成' : '兑换项目已取消并退款');
      await loadOrders();
      if (selectedOrder) await refreshSelectedOrder(selectedOrder.id);
    } catch (err) {
      setError(err.response?.data?.message || '更新兑换项目失败');
    } finally {
      setUpdating(false);
    }
  };

  const copyAddress = async (order) => {
    const addressText = buildAddressText(order);
    if (!addressText) return;
    try {
      await navigator.clipboard.writeText(addressText);
      showSuccess('收货地址已复制');
    } catch (err) {
      showError('复制失败，请手动选择地址文本');
    }
  };

  if (loading) {
    return <div className="loading">正在加载兑换订单...</div>;
  }

  return (
    <div className="admin-prize-orders">
      <BackButton to="/" />
      <header className="admin-prize-orders-header">
        <div>
          <h1>兑换订单管理</h1>
          <p>按订单查看兑换项目、备注、实体收货地址和处理状态。</p>
        </div>
      </header>

      {message && <div className="prizes-success">{message}</div>}
      {error && <div className="prizes-error">{error}</div>}

      <section className="prizes-metrics">
        <div><span>当前列表订单</span><strong>{metrics.orders}</strong></div>
        <div><span>待处理</span><strong>{metrics.pending}</strong></div>
        <div><span>部分处理</span><strong>{metrics.mixed}</strong></div>
        <div><span>含实体商品</span><strong>{metrics.physical}</strong></div>
      </section>

      <section className="admin-order-panel">
        <form
          className="admin-order-filters"
          onSubmit={(event) => {
            event.preventDefault();
            loadOrders();
          }}
        >
          <input
            placeholder="搜索订单号、用户、B站 UID、商品、地址或手机号"
            value={filters.search}
            onChange={(event) => setFilters({ ...filters, search: event.target.value })}
          />
          <select
            value={filters.status}
            onChange={(event) => setFilters({ ...filters, status: event.target.value })}
          >
            <option value="all">全部状态</option>
            <option value="pending">待处理</option>
            <option value="mixed">部分处理</option>
            <option value="completed">已完成</option>
            <option value="cancelled">已取消</option>
          </select>
          <select
            value={filters.delivery_type}
            onChange={(event) => setFilters({ ...filters, delivery_type: event.target.value })}
          >
            <option value="all">全部类型</option>
            <option value="physical">含实体商品</option>
            <option value="virtual">仅虚拟商品</option>
          </select>
          <button className="primary-action" type="submit">筛选</button>
        </form>

        <div className="admin-order-list">
          {orders.map((order) => (
            <button key={order.id} type="button" className="admin-order-row" onClick={() => openOrder(order.id)}>
              <div>
                <strong>订单 #{order.id}</strong>
                <span>{order.username} · {order.email || '无邮箱'}</span>
                <span>{order.bilibili_unames || order.bilibili_uids || '未记录B站账号'}</span>
              </div>
              <div>
                <strong>{formatCurrencyTotal(order.points_total, order.movie_ticket_total)}</strong>
                <span>{order.line_count} 个项目 · {order.item_count} 件</span>
                <span>{order.prize_names || '-'}</span>
              </div>
              <div>
                <em className={`delivery-badge ${Number(order.has_physical || 0) ? 'physical' : 'virtual'}`}>{deliveryLabel(order)}</em>
                <span className={`status-pill ${order.status}`}>{statusLabel(order.status)}</span>
              </div>
              <time>{formatDate(order.created_at)}</time>
            </button>
          ))}
          {orders.length === 0 && <div className="empty-inline">没有符合条件的订单</div>}
        </div>
      </section>

      {(selectedOrder || detailLoading) && (
        <div className="admin-order-modal" role="dialog" aria-modal="true" onClick={() => !updating && setSelectedOrder(null)}>
          <div className="admin-order-modal-content" onClick={(event) => event.stopPropagation()}>
            {detailLoading && !selectedOrder ? (
              <div className="empty-inline">正在加载订单详情...</div>
            ) : selectedOrder && (
              <>
                <button type="button" className="admin-order-close" onClick={() => setSelectedOrder(null)} disabled={updating}>×</button>
                <header className="admin-order-detail-header">
                  <div>
                    <p>订单 #{selectedOrder.id}</p>
                    <h2>{selectedOrder.username}</h2>
                    <span>{formatDate(selectedOrder.created_at)}</span>
                  </div>
                  <div className="admin-order-detail-status">
                    <span className={`status-pill ${selectedOrder.status}`}>{statusLabel(selectedOrder.status)}</span>
                    <strong>{formatCurrencyTotal(selectedOrder.points_total, selectedOrder.movie_ticket_total)}</strong>
                  </div>
                </header>

                {Number(selectedOrder.has_physical || 0) ? (
                  <section className="admin-order-address">
                    <div>
                      <h3>收货地址</h3>
                      <p>{buildAddressText(selectedOrder) || '这个实体订单没有地址快照'}</p>
                    </div>
                    {buildAddressText(selectedOrder) && (
                      <button type="button" className="secondary-action" onClick={() => copyAddress(selectedOrder)}>复制地址</button>
                    )}
                  </section>
                ) : (
                  <section className="admin-order-address virtual">
                    <h3>虚拟商品订单</h3>
                    <p>无需收货地址。</p>
                  </section>
                )}

                <section className="admin-order-items">
                  <h3>兑换项目</h3>
                  {(selectedOrder.items || []).map((item) => (
                    <article key={item.id} className="admin-order-item">
                      <img src={getImageUrl(item.prize_image)} alt={item.prize_name} />
                      <div>
                        <strong>{item.prize_name}</strong>
                        <span>{item.option_name || '默认规格'} · x{item.quantity || 1} · {formatLineCost(item)}</span>
                        <span className={`delivery-text ${item.delivery_type === 'virtual' ? 'virtual' : 'physical'}`}>
                          {item.delivery_type === 'virtual' ? '虚拟商品' : '实体商品'}
                        </span>
                        {item.user_remark && <p>备注：{item.user_remark}</p>}
                        {item.cancel_reason && <p>取消原因：{item.cancel_reason}</p>}
                      </div>
                      <div className="admin-order-item-actions">
                        <span className={`status-pill ${item.status}`}>{statusLabel(item.status)}</span>
                        {item.status === 'pending' ? (
                          <>
                            <button type="button" onClick={() => updateLineStatus(item, 'completed')} disabled={updating}>完成</button>
                            <button type="button" className="danger-text" onClick={() => updateLineStatus(item, 'cancelled')} disabled={updating}>取消</button>
                          </>
                        ) : (
                          <small>{item.handled_by_username || '已处理'}</small>
                        )}
                      </div>
                    </article>
                  ))}
                </section>

                {['pending', 'mixed'].includes(selectedOrder.status) && (
                  <footer className="admin-order-detail-actions">
                    <button type="button" className="btn btn-outline" onClick={() => updateOrderStatus(selectedOrder, 'cancelled')} disabled={updating}>
                      取消整单并退款
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => updateOrderStatus(selectedOrder, 'completed')} disabled={updating}>
                      完成整单
                    </button>
                  </footer>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPrizeOrders;
